import { MaintenanceWindowPatchManagementVendorProductSelectorView } from '../view/MaintenanceWindowPatchManagementVendorProductSelectorView';
import { templateMaintenanceWindowPatchManagementVendorProductSelector } from '../view/template';
import { MaintenanceWindowPatchManagementVendorProductSelectorRepository } from '../repository/MaintenanceWindowPatchManagementVendorProductSelectorRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowPatchManagementVendorProductSelectorWebComponent } from '..';
import { MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider } from '../dataProvider/MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider';
import { Product, VendorProductsPair } from '../types';
import { EnumListItem, SelectedOption } from '../../../ui/gzEnumList/types';

export class MaintenanceWindowPatchManagementVendorProductSelectorController extends BaseWebComponentController {
  public component: MaintenanceWindowPatchManagementVendorProductSelectorWebComponent;
  public dataRepository: MaintenanceWindowPatchManagementVendorProductSelectorRepository;
  public view: MaintenanceWindowPatchManagementVendorProductSelectorView;
  public viewConstructor: typeof MaintenanceWindowPatchManagementVendorProductSelectorView;
  public dataRepositoryConstructor: typeof MaintenanceWindowPatchManagementVendorProductSelectorRepository;

  public readonly ALL_PRODUCTS_PRODUCT_ID = '0';

  constructor(component: MaintenanceWindowPatchManagementVendorProductSelectorWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowPatchManagementVendorProductSelectorView,
      viewTemplate: templateMaintenanceWindowPatchManagementVendorProductSelector,
      dataRepositoryConstructor: MaintenanceWindowPatchManagementVendorProductSelectorRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$vendorSelector,
        event: 'open',
        callback: this.populateVendors.bind(this),
      },
      {
        element: this.view.dom.$vendorSelector,
        event: 'selectionChanged',
        callback: this.populateProducts.bind(this),
      },
      {
        element: this.view.dom.$productSelector,
        event: 'selectionChange',
        callback: this.vendorProductsSelected.bind(this),
      },
      {
        element: this.view.dom.$addButton,
        event: 'click',
        callback: this.addVendorProducts.bind(this),
      },

      {
        element: this.view.dom.$grid,
        event: 'deleteVendorProductsPair',
        callback: this.deleteVendorProductsPair.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: 'showVendorProductsPair',
        callback: this.showVendorProductsPair.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: 'editVendorProductsPair',
        callback: this.editVendorProductsPair.bind(this),
      },
    ];
  }

  async populateVendors() {
    const $vendorSelector = this.view.dom.$vendorSelector;

    if ($vendorSelector.isLoading || $vendorSelector.options.length) {
      return;
    }

    $vendorSelector.isLoading = true;
    $vendorSelector.isError = false;

    try {
      const vendors = await this.dataRepository.getAllVendors();

      $vendorSelector.options = this.view.getVendorOptions(vendors);
      $vendorSelector.isLoading = false;
    } catch (err) {
      console.error(err);
      $vendorSelector.isLoading = false;
      $vendorSelector.isError = true;
    }
  }

  async populateProducts() {
    const $vendorSelector = this.view.dom.$vendorSelector;
    const $productSelector = this.view.dom.$productSelector;

    this.view.dom.$addButton.setAttribute('disabled', 'disabled');

    // Before loading products list: set products selector disabled and clear options
    $productSelector.setAttribute('disabled', 'disabled');
    $productSelector.filterOptions = [];

    // Get selected vendor id
    const selectedVendorId = $vendorSelector.getFormValue();

    // Get products for vendor
    const products = await this.dataRepository.getProductsForVendor({ vendorId: selectedVendorId });

    // Set options
    $productSelector.filterOptions = this.view.getProductOptions(products);

    $productSelector.removeAttribute('disabled');

    if (this.isVendorInList(selectedVendorId)) {
      // Vendor already in grid
      $vendorSelector.invalid = true;
    } else {
      // Vendor not in grid
      $vendorSelector.invalid = false;
    }
  }

  vendorProductsSelected() {
    const $vendorSelector = this.view.dom.$vendorSelector;
    const $addButton = this.view.dom.$addButton;

    if ($vendorSelector.invalid) {
      // Vendor already in grid
      $addButton.setAttribute('disabled', 'disabled');
    } else {
      const selectedVendorId = $vendorSelector.getFormValue();

      if (this.isVendorInList(selectedVendorId)) {
        // Set vendor selector as invalid
        $vendorSelector.invalid = true;

        $addButton.setAttribute('disabled', 'disabled');
      } else {
        // Vendor not in grid
        $addButton.removeAttribute('disabled');
      }
    }
  }

  addVendorProducts() {
    const $vendorSelector = this.view.dom.$vendorSelector;
    const $productSelector = this.view.dom.$productSelector;

    const selectedVendor = $vendorSelector.selectedOption;
    const selectedVendorId = $vendorSelector.getFormValue();

    if (this.isVendorInList(selectedVendorId)) {
      return;
    }

    const selectedOptions = $productSelector.enumList.getCheckedOptions();

    let products: Product[] = [];

    if (selectedOptions.length === $productSelector.filterOptions.length) {
      products = [
        {
          id: this.ALL_PRODUCTS_PRODUCT_ID,
          name: this.getAllProductsName(),
        },
      ];
    } else {
      products = selectedOptions.map((option: EnumListItem) => {
        return {
          id: `${option.value}`,
          name: option.label,
        };
      });
    }

    const vendorProductsPairs = this.component.vendorProductsPairs;

    vendorProductsPairs.push({
      vendor: {
        id: selectedVendorId,
        name: selectedVendor.data.originalVendorName,
      },
      products,
      os: selectedVendor.data.os,
    } as VendorProductsPair);

    this.component.vendorProductsPairs = vendorProductsPairs;

    this.resetFormControls();
  }

  deleteVendorProductsPair(event: CustomEvent) {
    // Remove vendorId row from vendorProductPairs
    const vendorId = event.detail.vendorId;

    this.component.vendorProductsPairs = this.component.vendorProductsPairs.filter(
      (vendorProductsPair: VendorProductsPair) => vendorProductsPair.vendor.id !== vendorId
    );

    if (!this.isVendorInList(vendorId)) {
      // Vendor not in grid
      this.view.dom.$vendorSelector.invalid = false;

      if (this.view.dom.$productSelector.enumList.getCheckedOptions().length > 0) {
        this.view.dom.$addButton.removeAttribute('disabled');
      }
    }
  }

  async showVendorProductsPair(event: CustomEvent) {
    const vendorId = event.detail.vendorId;
    const $productSelector = event.detail.$productSelector;
    const selectedProducts = event.detail.selectedProducts;

    await this.dataRepository.getProductsForVendor({ vendorId });

    // Get products for vendor
    const products = await this.dataRepository.getProductsForVendor({ vendorId });

    const selectedProductIdsMap = selectedProducts.reduce((map, product: Product) => {
      map[product.id] = true;
      return map;
    }, {});

    // Set options
    $productSelector.filterOptions = this.view.getProductOptions(products, (productId: string): boolean => {
      if (selectedProductIdsMap[this.ALL_PRODUCTS_PRODUCT_ID]) {
        return true;
      }

      return selectedProductIdsMap[productId] || false;
    });

    $productSelector.removeAttribute('disabled');
  }

  editVendorProductsPair(event: CustomEvent) {
    // Vendor id
    const vendorId: string = event.detail.vendorId.toString();

    // GzEnumFilterWebComponent options - all available options
    const options: EnumListItem[] = event.detail.options;

    // GzEnumFilterWebComponent selected options
    const selectedOptions: SelectedOption[] = event.detail.selectedOptions;

    // Products selected in component value for vendor id
    const selectedProducts: Product[] = event.detail.selectedProducts;

    let newProducts: Product[] = [];

    // Check if all options were selected
    if (options.length === selectedOptions.length) {
      newProducts = [
        {
          id: this.ALL_PRODUCTS_PRODUCT_ID,
          name: this.getAllProductsName(),
        },
      ];
    } else {
      newProducts = selectedOptions.map((selectedOption: SelectedOption) => {
        return {
          id: `${selectedOption.value}`,
          name: selectedOption.label,
        };
      });
    }

    // Check if selection changed
    const newProductsIds: string = newProducts
      .map((product: Product) => product.id)
      .sort()
      .join();
    const selectedProductsIds: string = selectedProducts
      .map((product: Product) => product.id)
      .sort()
      .join();

    if (newProductsIds === selectedProductsIds) {
      // Product selection did not change
      return;
    }

    // Replace products for vendorId
    const vendorProductPairs = this.component.vendorProductsPairs;

    for (const vendorProductPair of vendorProductPairs) {
      if (vendorProductPair.vendor.id === vendorId) {
        vendorProductPair.products = newProducts;
        break;
      }
    }

    this.component.vendorProductsPairs = vendorProductPairs;
  }

  async getDataForView(): Promise<{
    gridDataProvider: MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider;
    disabled: boolean;
  }> {
    const gridDataProvider = new MaintenanceWindowPatchManagementVendorProductSelectorGridDataProvider(this.component);

    gridDataProvider.injectDependencies(this.dependencies);

    return { gridDataProvider, disabled: this.component.disabled };
  }

  private isVendorInList(vendorId: string): boolean {
    const selectedVendorId = this.view.dom.$vendorSelector.getFormValue();
    const selectedVendor = this.view.dom.$vendorSelector.selectedOption;

    // Check that vendor is not already added in the list (check by ID and by OS)
    const isVendorInList =
      this.component.vendorProductsPairs.filter((vendorProductPair: VendorProductsPair) => {
        return vendorProductPair.vendor.id === selectedVendorId && vendorProductPair.os === selectedVendor.data.os;
      }).length !== 0;

    return isVendorInList;
  }

  private getAllProductsName(): string {
    return this.dependencies.translateFn('All products');
  }

  public resetFormControls(): void {
    const $vendorSelector = this.view.dom.$vendorSelector;
    const $productSelector = this.view.dom.$productSelector;

    this.view.dom.$addButton.setAttribute('disabled', 'disabled');

    // clear selected value from vendor selector
    $vendorSelector.lastSelectedOption = undefined;
    $vendorSelector.selectedOption = undefined;
    $vendorSelector.clearInput();

    // clear selected products and disable selector
    $productSelector.filterOptions = [];
    $productSelector.disabled = true;
  }
}
